/**
 * Time presentation (OCP-536) - the customer-portal twin of
 * apps/admin/src/lib/format-time.ts.
 *
 * THE RULE: store and compute in UTC, present in Europe/Berlin with the zone
 * named. No database column, API payload or server clock changes; only
 * rendering goes through here. Do NOT "fix" a surface by writing local times
 * anywhere or by adding an offset - the IANA zone is what makes DST correct.
 *
 * The portal keeps its friendlier date style (`15 Jul 2026`), but every
 * date-TIME names the zone (`15 Jul 2026, 12:09 CEST`) and every calendar
 * day is the Berlin day - the Terms anchor maintenance and notice periods to
 * CET/CEST, so the customer reads the same clock the contract speaks.
 */

import { intlTag } from '../i18n';

export const DISPLAY_TIME_ZONE = 'Europe/Berlin';

type DateInput = string | number | Date | null | undefined;

function toDate(input: DateInput): Date | null {
  if (input === null || input === undefined || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `15 Jul 2026` - the calendar day in Europe/Berlin. */
export function fmtDate(input: DateInput, fallback = '-'): string {
  const d = toDate(input);
  if (!d) return fallback;
  return new Intl.DateTimeFormat(intlTag(), { timeZone: DISPLAY_TIME_ZONE, year: 'numeric', month: 'short', day: 'numeric' }).format(d);
}

/** `15 Jul 2026, 12:09 CEST` */
export function fmtDateTime(input: DateInput, fallback = '-'): string {
  const d = toDate(input);
  if (!d) return fallback;
  const parts = new Intl.DateTimeFormat(intlTag(), {
    timeZone: DISPLAY_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).formatToParts(d);
  const p: Record<string, string> = {};
  for (const x of parts) p[x.type] = x.value;
  if (p.hour === '24') p.hour = '00';
  return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute} ${p.timeZoneName}`;
}

/** `12:09 CEST` */
export function fmtTime(input: DateInput, fallback = '-'): string {
  const d = toDate(input);
  if (!d) return fallback;
  const parts = new Intl.DateTimeFormat(intlTag(), { timeZone: DISPLAY_TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short' }).formatToParts(d);
  const p: Record<string, string> = {};
  for (const x of parts) p[x.type] = x.value;
  if (p.hour === '24') p.hour = '00';
  return `${p.hour}:${p.minute} ${p.timeZoneName}`;
}
